import React from 'react';
import { useTranslation } from 'react-i18next';
import Dropdown from '../../../shared/components/forms/selectors/Dropdown';
import TimezoneSelector from '../../../shared/components/forms/selectors/TimezoneSelector';
import CustomDateFormatInput from './CustomDateFormatInput';
import ColumnDetectionControls from './ColumnDetectionControls';
import RequiredFieldIndicator from '../../../shared/components/forms/validation/RequiredFieldIndicator';
import { findDuplicateHeaders } from '../utils/findDuplicateHeaders';

interface ColumnMappingSectionProps {
  headers: string[];
  selectedColumn1: string;
  selectedColumn1Index: number;
  selectedColumn2: string;
  selectedColumn2Index: number;
  selectedColumn3: string;
  selectedColumn3Index: number;
  hasSeparateDateAndTime: boolean;
  selectedTimezone: string;
  valueColumnName: string;
  showCustomFormat: boolean;
  customDateFormat: string;
  columnValidationErrors?: {
    column1: boolean;
    column2: boolean;
    column3: boolean;
  };
  manualColumnSelection: boolean;
  onColumnChange?: () => void;
  onReDetectColumns: () => void;
  setSelectedColumn1: (value: string) => void;
  setSelectedColumn1Index: (value: number) => void;
  setSelectedColumn2: (value: string) => void;
  setSelectedColumn2Index: (value: number) => void;
  setSelectedColumn3: (value: string) => void;
  setSelectedColumn3Index: (value: number) => void;
  setSelectedTimezone: (value: string) => void;
  setValueColumnName: (value: string) => void;
  setShowCustomFormat: (value: boolean) => void;
  setCustomDateFormat: (value: string) => void;
  setManualColumnSelection: (value: boolean) => void;
  setHasSeparateDateAndTime: (value: boolean) => void;
}

const ColumnMappingSection: React.FC<ColumnMappingSectionProps> = (props) => {
  const {
    headers,
    selectedColumn1Index,
    selectedColumn2Index,
    selectedColumn3Index,
    hasSeparateDateAndTime,
    selectedTimezone,
    valueColumnName,
    showCustomFormat,
    customDateFormat,
    columnValidationErrors,
    manualColumnSelection,
    onColumnChange,
    onReDetectColumns,
    setSelectedColumn1,
    setSelectedColumn1Index,
    setSelectedColumn2,
    setSelectedColumn2Index,
    setSelectedColumn3,
    setSelectedColumn3Index,
    setSelectedTimezone,
    setValueColumnName,
    setShowCustomFormat,
    setCustomDateFormat,
    setManualColumnSelection,
    setHasSeparateDateAndTime
  } = props;
  const { t } = useTranslation('dataUpload');

  const duplicateGroups = React.useMemo(
    () => findDuplicateHeaders(headers),
    [headers]
  );

  if (headers.length === 0) return null;

  return (
    <>
      {duplicateGroups.length > 0 && (
        <div
          role="status"
          className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
        >
          {t('duplicateColumnsInfo', {
            names: duplicateGroups
              .map((g) => `${g.name} (${g.indices.length}×)`)
              .join(', '),
          })}
        </div>
      )}
      <div className="mb-4">
        {/* Timezone Selection */}
        <TimezoneSelector
          selectedTimezone={selectedTimezone}
          onTimezoneChange={setSelectedTimezone}
        />
      </div>

      {/* Column Detection Controls */}
      <ColumnDetectionControls
        manualColumnSelection={manualColumnSelection}
        hasSeparateDateAndTime={hasSeparateDateAndTime}
        onManualSelectionChange={setManualColumnSelection}
        onHasSeparateDateAndTimeChange={setHasSeparateDateAndTime}
        onReDetectColumns={onReDetectColumns}
      />

      {/* Column Dropdowns */}
      <div className="dropdowns-container space-y-4">
        {/* First Dropdown for Date */}
        <Dropdown
          options={headers.map((header, index) => ({
            value: `col_${index}`,
            label: `${header} (Spalte ${index + 1})`,
            key: `col_${index}`
          }))}
          selectedValue={`col_${selectedColumn1Index}`}
          onChange={(value: string) => {
            const index = parseInt(value.replace('col_', ''));
            if (index >= 0 && index < headers.length) {
              setSelectedColumn1(headers[index]);
              setSelectedColumn1Index(index);
              onColumnChange?.(); // Clear validation errors when user changes selection
            }
          }}
          label={t('columnSelection.date.label')}
          placeholder={t('columnSelection.date.placeholder')}
          hasError={columnValidationErrors?.column1 || false}
        />
        
        {/* Second Dropdown for Time/Value */}
        <Dropdown
          options={headers.map((header, index) => ({
            value: `col_${index}`,
            label: `${header} (Spalte ${index + 1})`,
            key: `col_${index}`
          }))}
          selectedValue={`col_${selectedColumn2Index}`}
          onChange={(value: string) => {
            const index = parseInt(value.replace('col_', ''));
            if (index >= 0 && index < headers.length) {
              setSelectedColumn2(headers[index]);
              setSelectedColumn2Index(index);
              onColumnChange?.(); // Clear validation errors when user changes selection
            }
          }}
          label={hasSeparateDateAndTime ? t('columnSelection.time.label') : t('columnSelection.value.label')}
          placeholder={hasSeparateDateAndTime ? t('columnSelection.time.placeholder') : t('columnSelection.value.placeholder')}
          hasError={columnValidationErrors?.column2 || false}
        />

        {/* Third Dropdown for Value when Time is separate */}
        {hasSeparateDateAndTime && (
          <Dropdown
            options={headers.map((header, index) => ({
              value: `col_${index}`,
              label: `${header} (Spalte ${index + 1})`,
              key: `col_${index}`
            }))}
            selectedValue={`col_${selectedColumn3Index}`}
            onChange={(value: string) => {
              const index = parseInt(value.replace('col_', ''));
              if (index >= 0 && index < headers.length) {
                setSelectedColumn3(headers[index]);
                setSelectedColumn3Index(index);
                onColumnChange?.(); // Clear validation errors when user changes selection
              }
            }}
            label={t('columnSelection.value.label')}
            placeholder={t('columnSelection.value.placeholder')}
            hasError={columnValidationErrors?.column3 || false}
          />
        )}

        {/* Value Column Name Input */}
        <div className="mt-4">
          <label htmlFor="value-column-name" className="delimiter-label">
            <RequiredFieldIndicator label={t('columnMapping.valueColumn.label')} ariaLabelKey="common.optionalField" />
          </label>
          <input
            type="text"
            id="value-column-name"
            value={valueColumnName}
            onChange={(e) => setValueColumnName(e.target.value)}
            placeholder={t('columnMapping.valueColumn.placeholder')}
            className="delimiter-input"
          />
        </div>
      </div>

      {/* Custom Date Format */}
      <CustomDateFormatInput
        show={showCustomFormat}
        customDateFormat={customDateFormat}
        onFormatChange={setCustomDateFormat}
        onClose={() => setShowCustomFormat(false)}
        onReset={() => setCustomDateFormat("")}
      />
    </>
  );
};

export default ColumnMappingSection;